import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sprout,
  Recycle,
  Leaf,
  TreePine,
  Users,
  Package,
  ArrowRight,
  MapPin,
  Trash,
  Newspaper,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/apiClient";
import DonationForm from "@/components/DonationForm";

const HERO_IMG =
  "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxoYW5kcyUyMHBsYW50aW5nJTIwc2FwbGluZyUyMGRpcnR8ZW58MHx8fHwxNzg2MjY3ODE4fDA&ixlib=rb-4.1.0&q=85";

const CATEGORY_CARDS = [
  { name: "Fruit & Veg Seeds", icon: Sprout, desc: "Mango pits, papaya, tomato & more in bulk." },
  { name: "Plastic Waste", icon: Recycle, desc: "Bottles, wrappers & containers by the kilo." },
  { name: "Paper & Card", icon: Newspaper, desc: "Old newspapers, cartons & office paper." },
  { name: "E-Waste & Metal", icon: Trash, desc: "Cables, cans, gadgets and scrap metal." },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const StatCard = ({ icon: Icon, value, label, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-stone-200 rounded-2xl p-6 flex flex-col gap-3"
  >
    <div className="h-11 w-11 rounded-xl bg-sage flex items-center justify-center">
      <Icon className="h-5 w-5 text-forest-dark" />
    </div>
    <div className="text-4xl font-serif text-forest-dark">{value}</div>
    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
  </div>
);

export default function PublicHome() {
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);

  const loadStats = useCallback(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadStats();
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => {});
  }, [loadStats]);

  const scrollToForm = () => {
    document.getElementById("donate")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative z-10 min-h-screen bg-bone">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-stone-200/60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="brand-logo">
            <div className="h-8 w-8 rounded-lg bg-forest flex items-center justify-center">
              <Leaf className="h-4 w-4 text-white" />
            </div>
            <span className="font-serif text-xl text-forest-dark">Paryavaran Suraksha</span>
          </div>
          <nav className="flex items-center gap-6">
            <a href="#impact" className="hidden sm:block text-sm text-forest-dark/80 hover:text-forest">
              Impact
            </a>
            <a href="#activities" className="hidden sm:block text-sm text-forest-dark/80 hover:text-forest">
              Activities
            </a>
            <Button
              onClick={scrollToForm}
              data-testid="nav-donate-btn"
              className="rounded-full bg-forest hover:bg-forest-dark text-white transition-transform hover:-translate-y-0.5"
            >
              Donate now
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-14 pb-20 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          initial="hidden"
          animate="show"
          variants={fade}
          className="lg:col-span-6 space-y-6"
        >
          <span className="inline-block text-xs uppercase tracking-[0.25em] text-terracotta font-medium">
            Give waste a second life
          </span>
          <h1 className="text-5xl sm:text-6xl font-serif leading-[1.05] text-forest-dark tracking-tight">
            Your throwaways can <span className="italic text-forest">reforest</span> the planet.
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
            Got fruit seeds you'd toss, or bulk plastic piling up? Donate them here. We collect from
            your doorstep and turn them into saplings, recycled materials and greener neighbourhoods.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={scrollToForm}
              data-testid="hero-donate-btn"
              className="rounded-full bg-forest hover:bg-forest-dark text-white px-7 py-6 text-base transition-transform hover:-translate-y-0.5"
            >
              Start donating <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <a href="#impact">
              <Button
                variant="outline"
                className="rounded-full border-forest text-forest hover:bg-forest hover:text-white px-7 py-6 text-base"
              >
                See our impact
              </Button>
            </a>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-6 relative"
        >
          <div className="rounded-[2rem] overflow-hidden border border-stone-200 shadow-sm">
            <img src={HERO_IMG} alt="Hands planting a sapling" className="w-full h-[420px] object-cover" />
          </div>
          <div className="absolute -bottom-5 -left-5 bg-white border border-stone-200 rounded-2xl px-5 py-4 hidden sm:block">
            <div className="text-3xl font-serif text-forest-dark" data-testid="hero-badge-count">
              {stats ? stats.total_donations : "—"}
            </div>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Donations pledged</div>
          </div>
        </motion.div>
      </section>

      {/* Categories */}
      <section className="max-w-6xl mx-auto px-6 pb-8">
        <h2 className="text-3xl font-serif text-forest-dark mb-6">What you can donate</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CATEGORY_CARDS.map((c, i) => (
            <motion.div
              key={c.name}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fade}
              data-testid={`category-card-${i}`}
              className="bg-white border border-stone-200 rounded-2xl p-6 hover:-translate-y-1 transition-transform"
            >
              <div className="h-12 w-12 rounded-xl bg-forest/10 flex items-center justify-center mb-4">
                <c.icon className="h-6 w-6 text-forest" />
              </div>
              <h3 className="text-xl font-serif text-forest-dark">{c.name}</h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Impact stats */}
      <section id="impact" className="max-w-6xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-3xl font-serif text-forest-dark">Our impact so far</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard
            testid="stat-trees"
            icon={TreePine}
            value={stats ? stats.impact.trees_planted.toLocaleString() : "—"}
            label="Trees planted"
          />
          <StatCard
            testid="stat-waste"
            icon={Recycle}
            value={stats ? `${stats.impact.waste_diverted_kg.toLocaleString()} kg` : "—"}
            label="Waste diverted"
          />
          <StatCard
            testid="stat-projects"
            icon={Package}
            value={stats ? stats.impact.projects_completed.toLocaleString() : "—"}
            label="Projects completed"
          />
          <StatCard
            testid="stat-volunteers"
            icon={Users}
            value={stats ? stats.impact.volunteers.toLocaleString() : "—"}
            label="Volunteers"
          />
        </div>
      </section>

      {/* Donation form */}
      <section id="donate" className="max-w-6xl mx-auto px-6 py-12">
        <div className="bg-forest-dark rounded-[2rem] overflow-hidden grid lg:grid-cols-12">
          <div className="lg:col-span-5 p-10 text-white flex flex-col justify-center">
            <span className="text-xs uppercase tracking-[0.25em] text-sage">Free doorstep pickup</span>
            <h2 className="text-4xl font-serif mt-3 mb-4 text-white">Donate your items</h2>
            <p className="text-white/80 leading-relaxed">
              List the items and quantities you want to give away, tell us where to collect them, and
              leave a contact number. Our team will reach out to arrange a pickup.
            </p>
          </div>
          <div className="lg:col-span-7 bg-bone p-8 sm:p-10">
            <DonationForm onSuccess={loadStats} />
          </div>
        </div>
      </section>

      {/* Activity feed */}
      <section id="activities" className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-serif text-forest-dark mb-6">Recent activities</h2>
        {posts.length === 0 ? (
          <div
            data-testid="no-posts"
            className="bg-white border border-dashed border-stone-300 rounded-2xl p-10 text-center text-muted-foreground"
          >
            No activities published yet. Check back soon to see what we've been up to.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p, i) => (
              <motion.article
                key={p.id}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fade}
                data-testid={`post-card-${p.id}`}
                className="bg-white border border-stone-200 rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform"
              >
                {p.image_url ? (
                  <img src={p.image_url} alt={p.title} className="w-full h-44 object-cover" />
                ) : (
                  <div className="w-full h-44 bg-sage flex items-center justify-center">
                    <Leaf className="h-10 w-10 text-forest/40" />
                  </div>
                )}
                <div className="p-6">
                  <span className="inline-block text-[10px] uppercase tracking-[0.2em] text-terracotta font-medium mb-2">
                    {p.category}
                  </span>
                  <h3 className="text-xl font-serif text-forest-dark leading-snug">{p.title}</h3>
                  {p.location && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                      <MapPin className="h-3 w-3" /> {p.location}
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed line-clamp-4">{p.body}</p>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-white/60">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-forest flex items-center justify-center">
              <Leaf className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="font-serif text-lg text-forest-dark">Paryavaran Suraksha</span>
          </div>
          <p className="text-sm text-muted-foreground">Turning everyday waste into a greener tomorrow.</p>
          <Link
            to="/admin/login"
            data-testid="admin-link"
            className="text-xs uppercase tracking-[0.2em] text-forest-dark/60 hover:text-forest"
          >
            Admin
          </Link>
        </div>
      </footer>
    </div>
  );
}
